// Cold email: who has never been contacted, what is stopping each one, and the buttons that move
// them forward.
//
// THE PAGE IS BUILT AROUND THE BLOCKERS, NOT THE QUEUE. Measured on the live base: 528 prospects
// have never been cold-emailed and 5 of them are actually writeable. A page that showed only the
// 5 would imply the other 523 do not exist, when in fact each is a record that needs one specific
// thing done to it. The blocker tally is therefore near the top and the queue table is last.
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../services";
import type { ColdItem, ColdPara, ColdQueue, ColdTemplates, Defined, RunResponse } from "../services/types";
import { useSession } from "../components/SessionContext";
import { PageHead, SectionHead } from "../components/dash/PageHead";
import { StatTiles } from "../components/dash/StatTiles";
import { DefinitionsPanel } from "../components/dash/DefinitionsPanel";
import { DataTable, type Column } from "../components/dash/DataTable";
import { ConfirmBar } from "../components/dash/ConfirmBar";
import { RunResults, useJobRunner } from "../components/dash/JobPanel";

const TILES = ["cold.total", "cold.ready", "cold.sendable", "cold.blocked", "cold.capacity_total"];

const PARA_ORDER: [string, string][] = [
  ["greeting", "Greeting"], ["p1", "P1 opener"], ["p2", "P2 guarantee"],
  ["p3", "P3 proof"], ["p4", "P4 call to action"], ["signoff", "Sign-off"],
];

type View = "ready" | "notready" | "all";
type Blocker = { reason: string; count: number };
type ParaRow = { key: string; label: string; text: string; note: string; locked: boolean };
type CapRow = { mailbox: string; left: number };

const BLOCKER_COLUMNS: Column<Blocker>[] = [
  { key: "reason", label: "Reason", cell: (b) => b.reason },
  { key: "count", label: "Records", num: true, width: 110, cell: (b) => b.count },
  { key: "fix", label: "What clears it", cell: (b) => FIXES[b.reason] ?? "" },
];

const PARA_COLUMNS: Column<ParaRow>[] = [
  {
    key: "part", label: "Part", width: 170,
    cell: (p) => <>{p.label}{p.locked ? <span className="dim">locked</span> : null}</>,
  },
  {
    key: "text", label: "Text",
    cell: (p) => <><div className="pre">{p.text}</div>{p.note ? <div className="dim">{p.note}</div> : null}</>,
  },
];

const CAP_COLUMNS: Column<CapRow>[] = [
  { key: "mailbox", label: "Mailbox", cell: (c) => c.mailbox },
  { key: "left", label: "Cold left today", num: true, width: 160, cell: (c) => c.left },
];

const QUEUE_COLUMNS: Column<ColdItem>[] = [
  {
    key: "show", label: "Show",
    cell: (i) => <>{i.channel || "(unnamed)"}{i.dream_client ? <span className="dim">dream client</span> : null}</>,
  },
  { key: "person", label: "Person", cell: (i) => i.person || i.host || "none" },
  { key: "email", label: "Email", cell: (i) => i.email || "none" },
  { key: "sender", label: "Sender", cell: (i) => (i.sender ? i.sender.split("@")[0] : "none") },
  {
    key: "state", label: "State", width: 300,
    cell: (i) =>
      i.sendable ? (
        <><span className="ok">sendable</span>{i.engine_eligible === false ? <span className="dim">engine will skip, lead not verified</span> : null}</>
      ) : i.ready ? (
        `ready, ${i.send_blocker}`
      ) : (
        i.blockers[0] || ""
      ),
  },
];

export default function ColdEmail() {
  const session = useSession();
  const runner = useJobRunner();
  const [arm, setArm] = useState("evidence");
  const [view, setView] = useState<View>("ready");
  const [preview, setPreview] = useState<RunResponse | null>(null);
  const force = useRef(false);

  const queueQ = useQuery({
    queryKey: ["cold:queue"],
    queryFn: async () => {
      const r = await api.cold.queue(0, "", false, force.current);
      force.current = false;
      if (!r.ok) throw new Error(r.error);
      return r as Defined<ColdQueue>;
    },
    staleTime: 120_000,
  });

  const tplQ = useQuery({
    queryKey: ["cold:templates"],
    queryFn: async () => {
      const r = await api.cold.templates();
      if (!r.ok) throw new Error(r.error);
      return r as unknown as ColdTemplates;
    },
    staleTime: 600_000,
  });

  const rows = useMemo(() => {
    const all = queueQ.data?.items ?? [];
    if (view === "ready") return all.filter((i) => i.ready);
    if (view === "notready") return all.filter((i) => !i.ready);
    return all;
  }, [queueQ.data, view]);

  function refresh() {
    force.current = true;
    void queueQ.refetch();
  }

  async function dryRun() {
    await runner.run("Dry run", () => api.cold.dryRun(0, "", arm));
  }

  async function previewAssign() {
    setPreview(null);
    const r = await api.cold.assignSenders(0, "", false);
    setPreview(r.ok ? r : { error: r.error });
  }

  async function assign() {
    const r = await runner.run("Assign senders", () => api.cold.assignSenders(0, "", true));
    setPreview(null);
    if (r) refresh();
  }

  const q = queueQ.data;
  const tpl = tplQ.data;
  const selected = tpl?.arms.find((a) => a.key === arm);
  const capacity: CapRow[] = Object.entries(q?.capacity ?? {}).map(([mailbox, left]) => ({ mailbox, left }));
  const canDraftCold = session?.role === "admin" && q?.draft_enabled === true;

  const paraRows: ParaRow[] = tpl
    ? [
        ...PARA_ORDER.flatMap(([key, label]) => {
          const p = tpl.structure[key] as ColdPara | undefined;
          if (!p || typeof p.text !== "string") return [];
          return [{ key, label, text: p.text, note: p.note ?? "", locked: !!p.locked }];
        }),
        { key: "subject", label: "Subject", text: tpl.subject, note: "", locked: false },
      ]
    : [];

  const previewText = preview
    ? preview.error
      ? String(preview.error)
      : JSON.stringify(stripOk(preview), null, 2)
    : "Reading the preview.";

  return (
    <>
      <PageHead
        title="Cold email"
        caption="Prospects who have never been emailed, read from Airtable. The locked copy is pulled live from DimeMotionOS on every load, so what is shown below is what would actually be written. Nothing on this page sends."
      />

      <section className="section compact">
        <SectionHead title="Today" />
        {queueQ.isError ? (
          <div className="note-neg"><p>{(queueQ.error as Error).message}</p></div>
        ) : !q ? (
          <p className="empty">Reading Airtable.</p>
        ) : (
          <>
            <StatTiles
              tiles={[
                { id: "cold.total", value: q.total },
                { id: "cold.ready", value: q.ready },
                { id: "cold.sendable", value: q.sendable },
                { id: "cold.blocked", value: q.blocked },
                { id: "cold.capacity_total", value: q.capacity_total },
              ]}
              definitions={q.definitions}
              readAt={q.read_at}
            />
            {q.ready > 0 && q.sendable === 0 ? (
              <div className="note">
                <p>
                  {q.ready} {q.ready === 1 ? "prospect is" : "prospects are"} fully researched but none can
                  be sent, because no sending mailbox is set on any of them. Assign senders below and they
                  become sendable immediately.
                </p>
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="section compact">
        <SectionHead
          title={`What is missing on the other ${q?.blocked ?? 0}`}
          caption="A record can appear under more than one reason. These are listed rather than filtered away, because each one is a job somebody has to do."
        />
        <DataTable
          columns={BLOCKER_COLUMNS}
          rows={q?.blockers ?? []}
          rowKey={(b) => b.reason}
          loading={!q && !queueQ.isError}
          loadingText="Reading Airtable."
          empty="Nothing is missing on any record."
        />
      </section>

      <section className="section compact">
        <SectionHead title="Template" />
        {tplQ.isError ? (
          <div className="note-neg"><p>{(tplQ.error as Error).message}</p></div>
        ) : null}
        <div className="kws">
          {(tpl?.arms ?? []).map((a) => (
            <button
              key={a.key}
              type="button"
              className={`kw${arm === a.key ? " on" : ""}`}
              disabled={!a.live}
              title={a.live ? a.summary : a.retired_reason}
              onClick={() => a.live && setArm(a.key)}
            >
              {a.label}{a.live ? "" : " (retired)"}
            </button>
          ))}
        </div>
        {selected ? <p className="sub-block">{selected.summary}</p> : null}
        {tpl && tpl.stored.length === 0 ? (
          <p className="sub-block">
            No saved templates exist yet. The shape below is the one the writer follows; the
            paragraphs marked locked are read from DimeMotionOS and cannot be edited here.
          </p>
        ) : null}
        <DataTable
          columns={PARA_COLUMNS}
          rows={paraRows}
          rowKey={(p) => p.key}
          loading={!tpl && !tplQ.isError}
          loadingText="Reading the locked copy."
          empty="The template has no parts."
        />
      </section>

      <section className="section compact">
        <SectionHead title="Capacity today" />
        <DataTable
          columns={CAP_COLUMNS}
          rows={capacity}
          rowKey={(c) => c.mailbox}
          loading={!q && !queueQ.isError}
          loadingText="Reading the roster."
          empty="No mailbox is active for cold right now."
        />
      </section>

      <section className="section compact">
        <SectionHead title="Run" />
        <div className="run">
          <button type="button" className="btn quiet" disabled={queueQ.isFetching || !!runner.busy} onClick={refresh}>
            {queueQ.isFetching ? "Reading" : "Refresh queue"}
          </button>
          <button type="button" className="btn" disabled={!!runner.busy || !q} onClick={dryRun}>
            Dry run
          </button>
          <ConfirmBar
            label="Assign senders"
            confirmLabel="Yes, write these to Airtable"
            note="Writes the Sender Account field on each ready record. Nothing sends."
            disabled={!!runner.busy || !q || q.ready === 0}
            busy={runner.busy === "Assign senders"}
            onArm={previewAssign}
            onCancel={() => setPreview(null)}
            onConfirm={assign}
          >
            <pre className="job-log">{previewText}</pre>
          </ConfirmBar>
          {canDraftCold ? (
            <ConfirmBar
              label="Draft cold"
              confirmLabel="Yes, draft cold now"
              note="Drafts only, capped per mailbox. Nothing sends."
              disabled={!!runner.busy}
              busy={runner.busy === "Draft cold"}
              onConfirm={() => runner.run("Draft cold", () => api.cold.draft(true))}
            />
          ) : (
            <p className="hint">
              Drafting cold email from this page is switched off on the box. Follow-ups can be drafted
              from a button because the engine has a drafting script with every gate in it; cold
              drafting is an agent run, and it stays off until the box passes its checks.
            </p>
          )}
        </div>
        <RunResults runner={runner} onFinish={refresh} />
      </section>

      <section className="section compact">
        <SectionHead
          title="Queue"
          right={
            <div className="kws">
              {(["ready", "notready", "all"] as View[]).map((v) => (
                <button key={v} type="button" className={`kw${view === v ? " on" : ""}`} onClick={() => setView(v)}>
                  {v === "ready" ? `Ready ${q?.ready ?? 0}` : v === "notready" ? `Not ready ${q?.blocked ?? 0}` : `All ${q?.total ?? 0}`}
                </button>
              ))}
            </div>
          }
        />
        <DataTable
          columns={QUEUE_COLUMNS}
          rows={rows.slice(0, 300)}
          rowKey={(i) => i.record}
          loading={queueQ.isPending}
          loadingText="Reading all the Airtable records. A cold cache takes about 45 seconds, then it is instant for 5 minutes."
          empty="Nothing under this filter."
          footer={rows.length > 300 ? `Showing the first 300 of ${rows.length}. Narrow the filter rather than scrolling.` : null}
        />
      </section>

      {q ? <DefinitionsPanel ids={[...TILES, "cold.capacity"]} definitions={q.definitions} readAt={q.read_at} /> : null}
    </>
  );
}

function stripOk(r: RunResponse): Record<string, unknown> {
  const { ok: _ok, ...rest } = r as RunResponse & { ok?: boolean };
  return rest;
}

// What actually clears each blocker. Written out because "no pitch points" tells you a field is
// empty, not who fills it or from where.
const FIXES: Record<string, string> = {
  "no episode chosen for the evidence opener":
    "Pick the episode and record its real view count. The opener quotes that figure and it is never estimated.",
  "email address never verified":
    "Run it through the verification chain: RSS owner, then Hunter, then Scrubby deep, then SMTP.",
  "lead disqualified": "Already ruled out. No action unless the call was wrong.",
  "no primary email": "Find the personal mailbox. A role inbox does not count.",
  "no pitch points": "Set the Pitch Points field from the channel research.",
  "no youtube channel on the record": "Add the channel URL so the view count can be read.",
  "no first name to greet": "Fill Host Name. It wins over Primary Person for the greeting.",
  "primary email is not a valid address": "Correct the address on the record.",
};
